import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Send as SendIcon } from '@mui/icons-material'
import integrationService from '../../services/integrationService'
import { getErrorMessage } from '../../utils/errorHandlers'

interface WhatsAppTemplateOption {
  id: string
  name: string
  variables: string[]
}

const WhatsAppTestMessage: React.FC = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplateOption[]>([])
  const [loading, setLoading] = useState(true)
  const [templateId, setTemplateId] = useState('')
  const [phone, setPhone] = useState('')
  const [sampleDataText, setSampleDataText] = useState('{}')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    integrationService
      .getTemplates({ channel: 'whatsapp' })
      .then((data) => setTemplates(data as unknown as WhatsAppTemplateOption[]))
      .finally(() => setLoading(false))
  }, [])

  const selectedTemplate = templates.find((t) => t.id === templateId)

  const handleSend = async () => {
    if (!templateId || !phone.trim()) return
    setSending(true)
    setResult(null)
    try {
      let sampleData: Record<string, any> = {}
      try {
        sampleData = JSON.parse(sampleDataText || '{}')
      } catch {
        setResult({ success: false, message: 'Sample data must be valid JSON' })
        setSending(false)
        return
      }
      const response = await integrationService.testTemplate(templateId, phone.trim(), sampleData)
      setResult({
        success: (response as any).success !== false,
        message: (response as any).message || 'Test message sent',
      })
    } catch (err: any) {
      setResult({
        success: false,
        message: getErrorMessage(err, 'Failed to send test message'),
      })
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box maxWidth={600}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Send a Test WhatsApp Message
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Pick a template, provide a phone number, and optionally sample data for its variables — this sends a real
          message via the configured Enotify connection.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            select
            label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            fullWidth
          >
            {templates.length === 0 && <MenuItem disabled value="">No WhatsApp templates found</MenuItem>}
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Recipient Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="919999999999"
            helperText="Include country code, digits only"
            fullWidth
          />

          <TextField
            label="Sample Data (JSON)"
            value={sampleDataText}
            onChange={(e) => setSampleDataText(e.target.value)}
            multiline
            minRows={3}
            helperText={
              selectedTemplate?.variables?.length
                ? `Variables used by this template: ${selectedTemplate.variables.join(', ')}`
                : 'e.g. {"customer_name": "Test User", "order_number": "SO-00001"}'
            }
            fullWidth
          />

          {result && (
            <Alert severity={result.success ? 'success' : 'error'}>{result.message}</Alert>
          )}

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={sending || !templateId || !phone.trim()}
            >
              {sending ? 'Sending...' : 'Send Test Message'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default WhatsAppTestMessage
